import React from 'react';

import { CallAttemptsSummary } from '@/services/assessmentsAPI';
import Stack from '@/components/Stack';
import Chip from '@/components/Chip';
import { Heading, Body, Small } from '@/components/Typography';

interface CallAttemptsSectionProps {
  summary: CallAttemptsSummary;
}

const CallAttemptsSection: React.FC<CallAttemptsSectionProps> = ({
  summary,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds == null) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCallStatus = (status: string | null) => {
    if (!status) return '—';
    return status
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Stack vertical gap={16}>
      <Heading>Call Attempts ({summary.total_attempts})</Heading>

      <Stack gap={16} wrap>
        <div
          style={{
            backgroundColor: '#f9f9f9',
            padding: '8px 16px',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
            {summary.total_attempts}
          </div>
          <Small color="light">Total</Small>
        </div>
        <div
          style={{
            backgroundColor: '#FFDFD8',
            padding: '8px 16px',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div
            style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC1515' }}
          >
            {summary.minimal_attempts}
          </div>
          <Small>Minimal</Small>
        </div>
        <div
          style={{
            backgroundColor: '#F5F2DD',
            padding: '8px 16px',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div
            style={{ fontSize: '20px', fontWeight: 'bold', color: '#DEA83E' }}
          >
            {summary.partial_attempts}
          </div>
          <Small>Partial</Small>
        </div>
      </Stack>

      {summary.attempts.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '2px solid #e0e0e0',
                  textAlign: 'left',
                }}
              >
                <th style={{ padding: '8px 12px' }}>Date</th>
                <th style={{ padding: '8px 12px' }}>Call Status</th>
                <th style={{ padding: '8px 12px' }}>Duration</th>
                <th style={{ padding: '8px 12px' }}>Questions</th>
                <th style={{ padding: '8px 12px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.attempts.map((attempt, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <Small>{formatDate(attempt.attempted_at)}</Small>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{formatCallStatus(attempt.call_status)}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{formatDuration(attempt.duration_seconds)}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>
                      {attempt.questions_answered}/{attempt.total_questions}
                    </Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {attempt.is_minimal ? (
                      <Chip color="error" label="Minimal" size="small" />
                    ) : attempt.is_partial ? (
                      <Chip color="warning" label="Partial" size="small" />
                    ) : (
                      <Chip color="success" label="Substantial" size="small" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Stack>
  );
};

export default CallAttemptsSection;
